import { del, get, set } from 'idb-keyval';

import { REST_SESSION_IDB_KEY_PREFIX } from '../../config';
import { ACCOUNT_SLOT } from '../../util/multiaccount';

// Holds the current bearer token + self user id for the REST provider. Module-
// scoped: this runs inside the API worker, one logical session per worker. The
// session is mirrored to IndexedDB (worker-accessible, unlike localStorage) so it
// survives a page reload — `loadPersistedSession` restores it on worker boot.

export type PersistedProfile = {
  id: string; firstName: string; lastName?: string; phoneNumber: string; isPremium?: boolean;
};
export type PersistedSession = { token: string; userId: string; profile: PersistedProfile };

// Keyed by account slot so each account gets its own isolated session in IndexedDB.
const SESSION_IDB_KEY = `${REST_SESSION_IDB_KEY_PREFIX}${ACCOUNT_SLOT || 1}`;

let token: string | undefined;
let selfUserId: string | undefined;

export function setSession(newToken: string, userId: string, profile?: PersistedProfile) {
  token = newToken;
  selfUserId = userId;
  // Persist only when we have the profile to rebuild the current user on reload.
  if (profile) void set(SESSION_IDB_KEY, { token: newToken, userId, profile } satisfies PersistedSession);
}

export function clearSession() {
  token = undefined;
  selfUserId = undefined;
  // Return the delete promise (don't fire-and-forget): a 401 logout must be able to await the
  // IndexedDB removal before re-initializing, or a racing `loadPersistedSession` on re-init reads
  // the stale token back and bounces the user straight back into the expired session.
  return del(SESSION_IDB_KEY);
}

export function getToken() {
  return token;
}

export function getSelfUserId() {
  return selfUserId;
}

// Restore a persisted session into memory on worker boot. Returns it when present
// (so the caller can skip the login screen), or undefined for a cold start.
export async function loadPersistedSession(): Promise<PersistedSession | undefined> {
  const saved = await get<PersistedSession>(SESSION_IDB_KEY);
  if (!saved?.token || !saved.userId || !saved.profile) return undefined;
  token = saved.token;
  selfUserId = saved.userId;
  return saved;
}

// Read-only probe (no module-state mutation) for "is there a persisted REST login?". Used by the
// main-thread global-state cache to decide whether to rehydrate on reload — `hasStoredSession()`
// only sees MTProto localStorage, which this REST fork never populates.
export async function hasPersistedSession(): Promise<boolean> {
  try {
    const saved = await get<PersistedSession>(SESSION_IDB_KEY);
    return Boolean(saved?.token && saved.userId && saved.profile);
  } catch {
    return false;
  }
}
